import React, { Fragment, useEffect, useState } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import SubmitButton from "../Forms/SubmitButton";
import { getBooking } from "../../store/booking/bookingActions";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ModalStatus(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { current_booking } = useSelector((state) => state.booking);
  const [status, setStatus] = useState("");

  // Job Trace: lazily-loaded Horizon job that sent this message.
  const [jobOpen, setJobOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState("");
  const [rawOpen, setRawOpen] = useState(false);

  useEffect(() => {
    axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}get_mb_status/${props.id}`,
    }).then((response) => {
      setStatus(response.data);
    });
  }, [props.id]);

  const loadJob = () => {
    const next = !jobOpen;
    setJobOpen(next);
    if (next && !job && !jobLoading) {
      setJobLoading(true);
      setJobError("");
      axios({
        method: "get",
        url: `${process.env.REACT_APP_API_URL}get_message_job/${props.id}`,
      })
        .then((response) => setJob(response.data))
        .catch(() => setJobError("Could not load job trace."))
        .finally(() => setJobLoading(false));
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div className="pr-8 w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900 mb-4"
                    >
                      Message Status
                    </Dialog.Title>
                    
                    {status?.platform && (
                      <div className="mb-3 p-3 bg-blue-50 rounded-md">
                        <div className="text-sm font-medium text-blue-900">
                          Platform: {status.platform}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="py-2 border-b">
                        <div className="font-medium text-gray-700 mb-1">Conversation ID:</div>
                        <div className="text-gray-900 text-sm font-mono break-all">
                          {status?.id}
                        </div>
                      </div>
                      
                      <div className="flex justify-between py-2 border-b">
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className={`px-2 py-1 text-sm font-semibold rounded ${
                          status?.status === 'Delivered' || status?.status === 'Read' 
                            ? 'bg-green-100 text-green-800'
                            : status?.status === 'Failed'
                            ? 'bg-red-100 text-red-800'
                            : status?.status === 'Sent'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {status?.status}
                        </span>
                      </div>

                      {status?.sent_at && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="font-medium text-gray-700">Sent:</span>
                          <span className="text-gray-900 text-sm">{new Date(status.sent_at).toLocaleString()}</span>
                        </div>
                      )}

                      {status?.delivered_at && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="font-medium text-gray-700">Delivered:</span>
                          <span className="text-gray-900 text-sm">{new Date(status.delivered_at).toLocaleString()}</span>
                        </div>
                      )}

                      {status?.read_at && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="font-medium text-gray-700">Read:</span>
                          <span className="text-gray-900 text-sm">{new Date(status.read_at).toLocaleString()}</span>
                        </div>
                      )}

                      {status?.failed_reason && (
                        <div className="py-2 border-b">
                          <span className="font-medium text-gray-700">Error:</span>
                          <div className="mt-1 p-2 bg-red-50 rounded text-sm text-red-800">
                            {status.failed_reason}
                          </div>
                        </div>
                      )}

                      {status?.note && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <div className="text-xs text-gray-600">
                            ℹ️ {status.note}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Job Trace — finds the Horizon queue job that sent this message */}
                    <div className="mt-4 border-t pt-3">
                      <button
                        type="button"
                        onClick={loadJob}
                        className="flex w-full items-center justify-between text-left font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
                      >
                        <span>Job Trace</span>
                        <span className="text-xs text-gray-400">
                          {jobOpen ? "Hide ▲" : "Show ▼"}
                        </span>
                      </button>

                      {jobOpen && (
                        <div className="mt-2">
                          {jobLoading && (
                            <div className="text-sm text-gray-500">
                              Searching Horizon…
                            </div>
                          )}

                          {jobError && (
                            <div className="text-sm text-red-700">{jobError}</div>
                          )}

                          {!jobLoading && !jobError && job && job.found === false && (
                            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600">
                              ℹ️ {job.note}
                            </div>
                          )}

                          {!jobLoading && !jobError && job && job.found && (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between py-1 border-b">
                                <span className="font-medium text-gray-700">Job:</span>
                                <span className="text-gray-900 text-right break-all">{job.name}</span>
                              </div>
                              <div className="flex justify-between py-1 border-b">
                                <span className="font-medium text-gray-700">Status:</span>
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                                  job.status === "completed"
                                    ? "bg-green-100 text-green-800"
                                    : job.status === "failed"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}>
                                  {job.status}
                                </span>
                              </div>
                              <div className="flex justify-between py-1 border-b">
                                <span className="font-medium text-gray-700">Queue:</span>
                                <span className="text-gray-900">{job.queue}</span>
                              </div>
                              {job.attempts != null && (
                                <div className="flex justify-between py-1 border-b">
                                  <span className="font-medium text-gray-700">Attempts:</span>
                                  <span className="text-gray-900">{job.attempts}</span>
                                </div>
                              )}
                              {job.completed_at && (
                                <div className="flex justify-between py-1 border-b">
                                  <span className="font-medium text-gray-700">Completed:</span>
                                  <span className="text-gray-900 text-sm">{new Date(job.completed_at).toLocaleString()}</span>
                                </div>
                              )}
                              {job.failed_at && (
                                <div className="flex justify-between py-1 border-b">
                                  <span className="font-medium text-gray-700">Failed:</span>
                                  <span className="text-gray-900 text-sm">{new Date(job.failed_at).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="py-1 border-b">
                                <div className="font-medium text-gray-700 mb-1">Job ID:</div>
                                <div className="text-gray-900 text-xs font-mono break-all">{job.id}</div>
                              </div>

                              {Array.isArray(job.tags) && job.tags.length > 0 && (
                                <div className="py-1 border-b">
                                  <div className="font-medium text-gray-700 mb-1">Tags:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {job.tags.map((t) => (
                                      <span key={t} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700 break-all">{t}</span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {job.exception && (
                                <div className="py-1">
                                  <div className="font-medium text-red-700 mb-1">Exception:</div>
                                  <pre className="p-2 bg-red-50 rounded text-xs text-red-800 whitespace-pre-wrap break-all max-h-48 overflow-auto">{job.exception}</pre>
                                </div>
                              )}

                              <button
                                type="button"
                                onClick={() => setRawOpen(!rawOpen)}
                                className="text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
                              >
                                {rawOpen ? "Hide raw payload" : "Show raw payload"}
                              </button>
                              {rawOpen && (
                                <pre className="mt-1 p-2 bg-gray-900 text-gray-100 rounded text-xs whitespace-pre-wrap break-all max-h-64 overflow-auto">
                                  {JSON.stringify(job.payload, null, 2)}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {status?.error?.code && (
                      <div className="mt-4 p-3 bg-red-50 rounded-md">
                        <div className="font-medium text-red-900">Error {status.error.code}</div>
                        <div className="text-sm text-red-700 mt-1">{status.error.description}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
